/**
 * Глобальні дії життєвого циклу записів (DAT-04, DAT-05; UX-14, UX-15; keyboard.md, розд. 1):
 * - `Ctrl+Z` / `⌘Z` поза полями — скасувати останню дію зі стека `undo-last.ts`, поки не минуло її вікно.
 *   У полі клавіша лишається скасуванням введення браузера;
 * - команда палітри «Кошик» — кошик людини (`/trash`, trash.html, кадр В1);
 * - команда палітри «Дублі» — черга дублів (`/duplicates`, DAT-07), лише з правом злиття.
 */
import { useNavigate } from '@tanstack/react-router';
import { useHotkeys } from '../app/hotkeys.ts';
import { GLOBAL } from '../app/keymap.ts';
import { shell as st } from '../i18n/uk/shell.ts';
import { undoText } from '../i18n/uk/undo.ts';
import { DUPLICATES_PATH, TRASH_PATH } from '../shell/nav-model.ts';
import { announce } from '../ui/notify/index.ts';
import { mergeQueueEntities, usePlatformMeta } from './meta.ts';
import { isOnline } from './network.ts';
import { runLastUndo } from './undo-last.ts';

/** Клавіша мовчки нічого не робить лише для зрячого: стан скасування — у живому регіоні. */
function undoLast() {
  if (!isOnline()) announce(undoText.offline);
  else if (!runLastUndo()) announce(undoText.nothing);
}

export function LifecycleKeys({ signedIn }: { signedIn: boolean }) {
  const navigate = useNavigate();
  const mergeQueue = mergeQueueEntities(usePlatformMeta({ optional: true }).data).length > 0;
  useHotkeys(
    signedIn
      ? [
          { ...GLOBAL.undoLast, label: st.undoLast, handler: undoLast },
          {
            label: st.trash,
            handler: () => void navigate({ to: TRASH_PATH }),
            command: { id: 'trash', group: 'nav' },
          },
          ...(mergeQueue
            ? [
                {
                  label: st.duplicates,
                  handler: () => void navigate({ to: DUPLICATES_PATH }),
                  command: { id: 'duplicates', group: 'nav' as const },
                },
              ]
            : []),
        ]
      : [],
    { scope: 'global' },
  );
  return null;
}
